import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import crossmap from "../../../assets/crossmap.svg";
import {
  GoogleMap,
  Marker,
  Circle,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import { MAPS_API_KEY } from "../../../components/mapKey";
import {
  getPositionWithFallback,
  useLocationPermissionSync,
} from "../../../utils/getPositionWithFallback";
import LocationPermissionModal from "../../../components/PermissionModal";

/* ---------- helpers ---------- */
const defaultCenter = { lat: 33.6844, lng: 73.0479 };
const circularRadius = 100;

const Location = ({ onChange, initialLat, initialLng }) => {
  // Initialize Google Maps
     const { isLoaded } = useJsApiLoader({
   googleMapsApiKey: MAPS_API_KEY,
   libraries: ["places"],
 });

  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [position, setPosition] = useState(defaultCenter);
  const [address, setAddress] = useState("Fetching address...");
  const [showFullMap, setShowFullMap] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
const isOnline = () => navigator.onLine;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  /* ---- Fetch structured address from lat/lng ---- */
  const fetchAddress = async ({ lat, lng }) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: { "User-Agent": "myapp/1.0 (contact@example.com)" },
        }
      );
      const data = await res.json();

      const addr = data?.address || {};

      const countryToContinent = (code) => {
        const mapping = {
          AF: "Africa",
          AS: "Asia",
          EU: "Europe",
          NA: "North America",
          SA: "South America",
          OC: "Oceania",
        };
        const continentCode = code?.toUpperCase()?.slice(0, 2);
        return mapping[continentCode] || "Unknown";
      };

      const getRegion = () =>
        addr.state_district || addr.region || addr.state || addr.county || "";

      const structured = {
        latitude: lat,
        longitude: lng,
        address: data?.display_name || "Address not found",
        streetAddress: [addr.road, addr.house_number, addr.neighbourhood]
          .filter(Boolean)
          .join(", "),
        city: addr.city || addr.town || addr.village || addr.suburb || "",
        province: addr.state || "",
        community: addr.county || "",
        postalCode: addr.postcode || "",
        country: addr.country || "",
        countryCode: addr.country_code?.toUpperCase() || "",
        region: getRegion(),
        continent: countryToContinent(addr.country_code),
      };

      console.log("📍 Selected Location with region/continent:", structured);

      setAddress(structured.address);
      if (addressRef.current) addressRef.current.value = structured.address;

      if (typeof onChange === "function") {
        onChange(structured);
      }
    } catch (err) {
      setAddress("Address not found");
    }
  };

  const handleLiveLocation = () => {
  if (!navigator.geolocation) {
    toast.error("Geolocation is not supported by your browser");
    return;
  }

  getPositionWithFallback(
    (position) => {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      setPosition(coords);
      fetchAddress(coords);
      // toast.success("Live location fetched");
    },
    (error) => {
      if (error.code === error.PERMISSION_DENIED) {
        setShowPermissionModal(true);
      } else {
        toast.error(
          t("locationFetchFailed") ||
            "Unable to fetch your location. Please make sure Location Services are on and try again."
        );
      }
      console.error(error);
    }
  );
};

  useLocationPermissionSync({
    onGranted: () => {
      // Only refetch if the modal was showing, so a manually placed pin is not moved
      if (showPermissionModal) {
        setShowPermissionModal(false);
        handleLiveLocation();
      }
    },
    onDenied: () => setShowPermissionModal(true),
  });
  /* ---- Handle place selection from autocomplete ---- */
  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setPosition(coords);
        fetchAddress(coords);
        setSearchQuery(place.formatted_address || place.name || "");
      } else {
        toast.error("No details available for this place");
      }
    }
  };

  /* ---- Handle search location ---- */
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a location");
      return;
    }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}`,
        {
          headers: { "User-Agent": "myapp/1.0 (contact@example.com)" },
        }
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
        setPosition(coords);
        fetchAddress(coords);
      } else {
        toast.error("Location not found");
      }
    } catch {
      toast.error("Error fetching location");
    }
  };

  /* ---- Init: set API lat/lng if available ---- */
useEffect(() => {
 if (initialLat != null && initialLng != null) {
 const coords = {
    lat: Number(initialLat),
    lng: Number(initialLng),
  };    setPosition(coords);
    fetchAddress(coords);
  }
  // ❌ no else fallback to defaultCenter here
}, [initialLat, initialLng]);

  return (
    <Box>
      {/* Header with actions */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <CustomText
          customStyle={{
            ...textStyles.body4,
            color: theme.palette.text.primary,
          }}
        >
          {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
        </CustomText>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => setSearchBarOpen((prev) => !prev)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>

            <IconButton
    onClick={handleLiveLocation}
    sx={{
      backgroundColor: "#1976d2",
      color: "#fff",
      borderRadius: 1,
      width: 40,
      height: 31,
      "&:hover": { backgroundColor: "#115293" },
    }}
  >
    <RoomIcon fontSize="small" />
  </IconButton>

          <CustomButton
            label={t("fullMap")}
            size="small"
            sx={{
              height: 31,
              px: 2,
              backgroundColor: "#1976d2",
              color: "#fff",
              fontSize: 12,
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={() => setShowFullMap(true)}
          />
        </Box>
      </Box>

      {/* Search input with Autocomplete */}
      {searchBarOpen && (
        <Box sx={{ mb: 2 }}>
          {isLoaded ? (
            <Autocomplete
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={handlePlaceSelect}
            >
              <input
                type="text"
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Don't call handleSearch if autocomplete is active
                    // Let Google Places handle it
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "15px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  fontFamily: "Poppins, sans-serif",
                }}
              />
            </Autocomplete>
          ) : (
            <input
              type="text"
              placeholder="Loading..."
              disabled
              style={{
                width: "100%",
                padding: "10px 14px",
                fontSize: "15px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                fontFamily: "Poppins, sans-serif",
                backgroundColor: "#f5f5f5",
              }}
            />
          )}
        </Box>
      )}

      {/* Map preview */}
      <Box sx={{ height: 300, borderRadius: 2, overflow: "hidden" }}>
        {!isLoaded ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            Loading map...
          </Box>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={position}
            zoom={15}
          >
            <Marker
              position={position}
              draggable
              onDragEnd={(e) => {
                const newPos = {
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                };
                setPosition(newPos);
                fetchAddress(newPos);
              }}
            />
            <Circle
              center={position}
              radius={circularRadius}
              options={{
                fillColor: "#3f51b5",
                fillOpacity: 0.2,
                strokeColor: "#3f51b5",
                strokeOpacity: 0.5,
              }}
            />
          </GoogleMap>
        )}
      </Box>

      {/* Address */}
      <textarea
        ref={addressRef}
        type="text"
        readOnly
        value={address}
     style={{
             width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    marginTop: "10px",
    backgroundColor: "#f4f4f4",
    fontFamily: "Poppins, sans-serif",
    resize: "vertical",      // allow manual resize if needed
    overflowY: "auto",       // show scroll when text exceeds height
    lineHeight: "1.4",
        }}
      />

      <LocationPermissionModal
        open={showPermissionModal}
        onCancel={() => setShowPermissionModal(false)}
      />

      {/* Full Map */}
      {showFullMap && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#fff",
            zIndex: 1300,
          }}
        >
          <IconButton
            onClick={() => setShowFullMap(false)}
            sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}
          >
            <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
          </IconButton>
          {!isLoaded ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              Loading map...
            </Box>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={position}
              zoom={15}
            >
              <Marker
                position={position}
                draggable
                onDragEnd={(e) => {
                  const newPos = {
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  };
                  setPosition(newPos);
                  fetchAddress(newPos);
                }}
              />
              <Circle
                center={position}
                radius={circularRadius}
                options={{
                  fillColor: "#3f51b5",
                  fillOpacity: 0.2,
                  strokeColor: "#3f51b5",
                  strokeOpacity: 0.5,
                }}
              />
            </GoogleMap>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Location;
